import React, { useState, useRef } from 'react';
import { FonctionArmement, Competence, Service, CompetenceFonctionArmement } from '../../../../../../shared/types/config';

interface CompetenceFonctionArmementWithTempId extends CompetenceFonctionArmement {
  _tempId?: number;
}

interface FonctionArmementModalProps {
  editingFonctionArmement: FonctionArmement | null;
  fonctionArmementForm: Omit<FonctionArmement, 'id'>;
  setFonctionArmementForm: React.Dispatch<React.SetStateAction<Omit<FonctionArmement, 'id'>>>;
  competences: Competence[];
  services: Service[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

const inputCls = 'w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1.5';

const FonctionArmementModal: React.FC<FonctionArmementModalProps> = ({
  editingFonctionArmement,
  fonctionArmementForm,
  setFonctionArmementForm,
  competences,
  services,
  onClose,
  onSave,
}) => {
  const [filterServiceFonctionArmement, setFilterServiceFonctionArmement] = useState<number | 'all'>('all');
  const tempIdCounter = useRef<number>(0);

  const addTempIdsToCompetences = (comps: CompetenceFonctionArmement[]): CompetenceFonctionArmementWithTempId[] => {
    return comps.map((comp, index) => {
      const c = comp as CompetenceFonctionArmementWithTempId;
      return { ...c, _tempId: c._tempId !== undefined ? c._tempId : Date.now() + index };
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 rounded-xl border border-[#243052]/90 bg-[#0a1128] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e2d4d]">
          <h2 className="text-base font-bold text-white">
            {editingFonctionArmement ? "Modifier la fonction d'armement" : "Ajouter une fonction d'armement"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1.5 hover:bg-[#1e2d4d] rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)]">
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Nom *</label>
              <input
                type="text"
                value={fonctionArmementForm.nom}
                onChange={(e) => setFonctionArmementForm({ ...fonctionArmementForm, nom: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea
                value={fonctionArmementForm.description}
                onChange={(e) => setFonctionArmementForm({ ...fonctionArmementForm, description: e.target.value })}
                className={`${inputCls} resize-none`}
                rows={3}
                placeholder="Description optionnelle..."
              />
            </div>
            <div>
              <label className={labelCls}>
                Compétences requises (optionnel)
                {fonctionArmementForm.id_competences && fonctionArmementForm.id_competences.length > 0 && (
                  <span className="ml-2 text-xs text-[#8ea0f5]">
                    ({fonctionArmementForm.id_competences.length} sélectionnée{fonctionArmementForm.id_competences.length > 1 ? 's' : ''})
                  </span>
                )}
              </label>
              {competences.length > 10 && (
                <div className="mb-3">
                  <select
                    value={filterServiceFonctionArmement}
                    onChange={(e) => setFilterServiceFonctionArmement(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    className={inputCls}
                  >
                    <option value="all">Tous les services</option>
                    <option value={0}>Générales (tous services)</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>{service.nom}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4 max-h-64 overflow-y-auto">
                {competences.length === 0 ? (
                  <p className="text-slate-400 text-sm">Aucune compétence disponible</p>
                ) : (() => {
                  const filteredCompetences = competences.filter(competence => {
                    if (filterServiceFonctionArmement === 'all') return true;
                    if (filterServiceFonctionArmement === 0) return !competence.id_service;
                    return competence.id_service === filterServiceFonctionArmement;
                  });

                  const competencesByService = filteredCompetences.reduce((acc, competence) => {
                    const serviceId = competence.id_service || 0;
                    const serviceName = competence.id_service
                      ? services.find(s => s.id === competence.id_service)?.nom || `Service ${serviceId}`
                      : 'Générales (tous services)';
                    if (!acc[serviceName]) acc[serviceName] = [];
                    acc[serviceName].push(competence);
                    return acc;
                  }, {} as Record<string, Competence[]>);

                  Object.keys(competencesByService).forEach(serviceName => {
                    competencesByService[serviceName].sort((a, b) => {
                      const ordreA = a.ordre ?? 999999;
                      const ordreB = b.ordre ?? 999999;
                      if (ordreA !== ordreB) return ordreA - ordreB;
                      return a.nom.localeCompare(b.nom);
                    });
                  });

                  const serviceNames = Object.keys(competencesByService).sort();

                  if (filteredCompetences.length === 0) {
                    return <p className="text-slate-400 text-sm text-center py-4">Aucune compétence trouvée pour ce service</p>;
                  }

                  return (
                    <div className="space-y-4">
                      {serviceNames.map(serviceName => (
                        <div key={serviceName}>
                          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 pb-1 border-b border-[#1e2d4d]">
                            {serviceName}
                          </div>
                          <div className="space-y-1">
                            {competencesByService[serviceName].map(competence => {
                              const competenceCount = fonctionArmementForm.id_competences?.filter((c: CompetenceFonctionArmement) => c.id === competence.id).length || 0;
                              return (
                                <div key={competence.id} className="flex items-center gap-2 text-slate-300 hover:text-white p-2 rounded hover:bg-[#1e2d4d]/50 transition-colors">
                                  <div className="flex-1 flex items-center gap-2">
                                    <span className="text-sm">{competence.nom}</span>
                                    {competence.is_specialite && (
                                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded text-xs">Spécialité</span>
                                    )}
                                    {competenceCount > 0 && (
                                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/40 rounded text-xs">{competenceCount}x</span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentCompetences = addTempIdsToCompetences(fonctionArmementForm.id_competences || []);
                                      if (competence.id) {
                                        const maxOrdre = currentCompetences.length > 0 ? Math.max(...currentCompetences.map((c) => c.ordre)) : 0;
                                        tempIdCounter.current += 1;
                                        const competencesWithoutTempIds = currentCompetences.map(({ _tempId, ...rest }) => rest);
                                        setFonctionArmementForm({
                                          ...fonctionArmementForm,
                                          id_competences: [...competencesWithoutTempIds, { id: competence.id, ordre: maxOrdre + 1, optionnel: false, ref: false }],
                                        });
                                      }
                                    }}
                                    className="rounded border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-2 py-1 text-xs font-medium text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors flex items-center gap-1"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Ajouter
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {fonctionArmementForm.id_competences && fonctionArmementForm.id_competences.length > 0 && (
                <div className="mt-4">
                  <label className={labelCls}>Ordre des compétences sélectionnées</label>
                  <div className="rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4 space-y-2">
                    {(() => {
                      const competencesArray = fonctionArmementForm.id_competences || [];
                      const competencesWithIndex = competencesArray.map((comp, originalIndex) => ({ ...comp, _originalIndex: originalIndex }));
                      return [...competencesWithIndex]
                        .sort((a, b) => a.ordre - b.ordre)
                        .map((compItem) => {
                          const competence = competences.find((c: Competence) => c.id === compItem.id);
                          const uniqueKey = `${compItem.id}-${compItem._originalIndex}-${compItem.ordre}-${compItem.optionnel}-${compItem.ref}`;
                          return competence ? (
                            <div key={uniqueKey} className="flex items-center gap-3 p-2 rounded border border-[#1e2d4d]/60 bg-[#0a132b]/60">
                              <div className="flex-1 flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-400 w-6">{compItem.ordre}.</span>
                                <span className="text-sm text-white">
                                  {competence.nom}
                                  {compItem.optionnel && <span className="ml-2 text-xs text-amber-400 italic">(optionnel)</span>}
                                  {compItem.ref && <span className="ml-2 text-xs text-emerald-400 italic">(ref)</span>}
                                </span>
                              </div>
                              <input
                                type="number"
                                min="1"
                                value={compItem.ordre}
                                onChange={(e) => {
                                  const newOrdre = parseInt(e.target.value) || 1;
                                  setFonctionArmementForm({
                                    ...fonctionArmementForm,
                                    id_competences: competencesArray.map((c, idx) => idx === compItem._originalIndex ? { ...c, ordre: newOrdre } : c),
                                  });
                                }}
                                className="w-14 rounded border border-[#2a3a62] bg-[#0a132b] px-2 py-1 text-xs text-slate-100 focus:outline-none"
                              />
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={compItem.optionnel || false}
                                    onChange={(e) => {
                                      setFonctionArmementForm({
                                        ...fonctionArmementForm,
                                        id_competences: competencesArray.map((c, idx) => idx === compItem._originalIndex ? { ...c, optionnel: e.target.checked } : c),
                                      });
                                    }}
                                    className="w-3.5 h-3.5"
                                  />
                                  <span className="text-xs text-amber-400">Opt.</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={compItem.ref || false}
                                    onChange={(e) => {
                                      setFonctionArmementForm({
                                        ...fonctionArmementForm,
                                        id_competences: competencesArray.map((c, idx) => idx === compItem._originalIndex ? { ...c, ref: e.target.checked } : c),
                                      });
                                    }}
                                    className="w-3.5 h-3.5"
                                  />
                                  <span className="text-xs text-emerald-400">Ref</span>
                                </label>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setFonctionArmementForm({
                                    ...fonctionArmementForm,
                                    id_competences: competencesArray.filter((_, idx) => idx !== compItem._originalIndex),
                                  });
                                }}
                                className="rounded border border-red-500/35 bg-red-500/10 p-1 text-red-300 hover:bg-red-500/20 transition-colors"
                                title="Supprimer"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : null;
                        });
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#1e2d4d]">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors"
          >
            {editingFonctionArmement ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FonctionArmementModal;
